import { MenuOutlined } from "@ant-design/icons"
import { Button, Col, Drawer, Grid, Layout, Row, Space, Switch } from "antd"
import { Suspense, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { themeMode } from "../../store/Theme/ThemeSlice"
import { setLanguage } from "../../store/Language/LanguageSlice"
import { isAuthenticated } from "../../utilities/authentication"
import NavBar from "../NavBars/NavBar"
import SideBar from "../NavBars/SideBar"
import Footer from "../Footer/Footer"
import DentistPortalGuard from "./DentistPortalGuard"
import Loading from "../Loading/Loading"
import useTranslation from "../../localization/useTranslation"
import { getShellCssVars, shell, sidebar } from "../../theme/palette"
import ShellDataProvider from "../../query/ShellDataProvider"
import OnboardingTour from "../Onboarding/OnboardingTour"
import SupportChatWidget from "../Support/SupportChatWidget"
import "../../styles/privateLayout.css"

const SIDER_WIDTH = 250
const { useBreakpoint } = Grid

const PrivateRoute = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [login, setLogin] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { Header, Sider, Content } = Layout
  const { theme } = useSelector((state) => state.theme)
  const { language } = useSelector((state) => state.language)
  const { showPrices } = useSelector((state) => state.priceVisibility)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const screens = useBreakpoint()
  // Treat unknown (SSR/first paint) as mobile so phones never get a 250px content offset flash.
  const isMobile = screens.lg !== true
  const isDark = theme === "dark"
  const colors = isDark ? shell.dark : shell.light
  const cssVars = useMemo(() => getShellCssVars(theme), [theme])
  const isRtl = language === "ar"

  useEffect(() => {
    if (isAuthenticated()) {
      setLogin(true)
    } else {
      setLogin(false)
      navigate("/")
    }
  }, [navigate])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false)
    }
  }, [isMobile])

  useEffect(() => {
    const onOpen = () => setMobileMenuOpen(true)
    const onClose = () => setMobileMenuOpen(false)
    window.addEventListener("dentrist:open-mobile-menu", onOpen)
    window.addEventListener("dentrist:close-mobile-menu", onClose)
    return () => {
      window.removeEventListener("dentrist:open-mobile-menu", onOpen)
      window.removeEventListener("dentrist:close-mobile-menu", onClose)
    }
  }, [])

  const changeTheme = (checked) => {
    if (checked) {
      dispatch(themeMode("dark"))
    } else {
      dispatch(themeMode("light"))
    }
  }

  const toggleLanguage = (checked) => {
    const nextLanguage = checked ? "ar" : "en"
    dispatch(setLanguage(nextLanguage))
  }

  const siderStyle = {
    paddingTop: "4px",
    backgroundColor: sidebar.bg,
    color: sidebar.text,
    borderLeft: isRtl ? `1px solid ${sidebar.border}` : "none",
    borderRight: isRtl ? "none" : `1px solid ${sidebar.border}`,
    ...(isRtl ? { right: 0, left: "auto" } : { left: 0, right: "auto" }),
  }

  const contentOffsetStyle = isMobile
    ? { marginLeft: 0, marginRight: 0, backgroundColor: colors.contentBg }
    : {
        marginRight: isRtl ? SIDER_WIDTH : 0,
        marginLeft: isRtl ? 0 : SIDER_WIDTH,
        backgroundColor: colors.contentBg,
      }

  if (!login) {
    return null
  }

  return (
    <ShellDataProvider>
      <Layout
        className="private-app-layout"
        data-theme={theme}
        style={{
          height: "auto",
          minHeight: "100vh",
          backgroundColor: colors.layoutBg,
          direction: isRtl ? "rtl" : "ltr",
          ...cssVars,
        }}
      >
        {!isMobile ? (
          <Sider
            className="private-sider"
            style={siderStyle}
            width={SIDER_WIDTH}
            theme="dark"
          >
            <SideBar />
          </Sider>
        ) : (
          <Drawer
            className="private-mobile-drawer"
            title={t("ui.menu.navigation")}
            placement={isRtl ? "right" : "left"}
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            width={Math.min(SIDER_WIDTH, typeof window !== "undefined" ? window.innerWidth - 48 : SIDER_WIDTH)}
            bodyStyle={{ padding: 0, backgroundColor: sidebar.bg }}
            headerStyle={{
              backgroundColor: sidebar.bg,
              color: sidebar.text,
              borderBottom: `1px solid ${sidebar.border}`,
            }}
            destroyOnClose={false}
            forceRender
          >
            <SideBar />
          </Drawer>
        )}

        <Layout className="private-inner-layout" style={contentOffsetStyle}>
          <Header
            className="private-header"
            style={{
              backgroundColor: colors.chromeBg,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <div className="private-header__bar" style={{ color: colors.text }}>
              <div
                className="private-header__start"
                style={{
                  order: isRtl ? 1 : 0,
                  direction: isRtl ? "rtl" : "ltr",
                }}
              >
                {isMobile ? (
                  <Button
                    className="private-header__menu-btn"
                    type="text"
                    aria-label={t("ui.menu.open")}
                    icon={<MenuOutlined />}
                    onClick={() => setMobileMenuOpen(true)}
                    style={{ color: colors.text }}
                  />
                ) : null}
                <Space
                  id="tour-header-controls"
                  className="private-header__switches"
                  size={isMobile ? 8 : 12}
                  style={{ direction: isRtl ? "rtl" : "ltr" }}
                >
                  <Switch
                    checkedChildren="AR"
                    unCheckedChildren="EN"
                    checked={language === "ar"}
                    onChange={toggleLanguage}
                    style={{ direction: isRtl ? "rtl" : "ltr" }}
                  />
                  <Switch
                    checkedChildren={t("ui.theme.lightMode")}
                    unCheckedChildren={t("ui.theme.darkMode")}
                    checked={theme === "dark"}
                    onChange={changeTheme}
                    style={{ direction: isRtl ? "rtl" : "ltr" }}
                  />
                </Space>
              </div>
              <div
                id="tour-navbar"
                className="private-header__end"
                style={{
                  order: isRtl ? 0 : 1,
                  justifyContent: isRtl ? "flex-start" : "flex-end",
                }}
              >
                <NavBar compact={isMobile} />
              </div>
            </div>
          </Header>

          <Row justify="center" style={{ width: "100%", margin: 0 }}>
            <Col xs={24} sm={24} md={23} lg={23} className="private-content-shell">
              <Content
                id="tour-content"
                style={{ minHeight: "80vh", backgroundColor: colors.contentBg }}
              >
                <Row>
                  <Col span={24} style={{ paddingTop: isMobile ? 12 : 20, paddingInline: isMobile ? 12 : 0 }}>
                    <DentistPortalGuard />
                    {/* Suspense must wrap only the page Outlet — never the shell — so
                        lazy route chunks swap content without remounting sidebar/header. */}
                    <Suspense fallback={<Loading />}>
                      <Outlet key={showPrices ? "prices-visible" : "prices-hidden"} />
                    </Suspense>
                  </Col>
                </Row>
              </Content>
            </Col>
          </Row>

          <Row className="private-footer-row">
            <Col>
              <Footer />
            </Col>
          </Row>
        </Layout>
        <SupportChatWidget />
      </Layout>
      <OnboardingTour />
    </ShellDataProvider>
  )
}

export default PrivateRoute
